import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LuGripVertical, LuPencil, LuX } from 'react-icons/lu';
import type { CustomField } from '../types';
import { getFieldTypeIcon, getFieldTypeLabel } from '../utils/fieldTypeHelpers';
import CustomFieldPreview from './CustomFieldPreview';

interface CustomFieldItemProps {
  field: CustomField;
  onEditField: (field: CustomField) => void;
  onDeleteField: (field: CustomField) => void;
}

/**
 * ドラッグ可能なカスタムフィールドアイテム
 */
export default function CustomFieldItem({
  field,
  onEditField,
  onDeleteField,
}: CustomFieldItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-6 border-b border-solid border-gray-100 bg-white px-3 py-4 first:border-t first:border-solid"
    >
      {/* ドラッグハンドル */}
      <div
        className="flex cursor-grab items-center self-stretch active:cursor-grabbing"
        {...listeners}
        {...attributes}
      >
        <LuGripVertical size={20} className="text-gray-400" />
      </div>

      {/* フィールド名と必須タグ */}
      <div className="w-[350px] space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-800">{field.name}</span>
          {field.is_required && (
            <span className="h-5 rounded-full bg-red-50 px-3 text-xs leading-5 font-bold whitespace-nowrap text-red-600">
              必須
            </span>
          )}
        </div>
        {/* 入力プレビュー */}
        <CustomFieldPreview field={field} />
      </div>

      {/* フィールドタイプと選択肢 */}
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <div className="flex h-6 items-center gap-1 rounded-full bg-gray-100 px-2">
            {getFieldTypeIcon(field.field_type)}
            <span className="text-xs text-gray-700">{getFieldTypeLabel(field.field_type)}</span>
          </div>
        </div>
        {field.custom_field_options.length > 0 && (
          <p className="pt-2 text-xs text-gray-600">
            {field.custom_field_options.map((opt) => opt.value).join(', ')}
          </p>
        )}
      </div>

      {/* アクションボタン */}
      <div className="flex w-20 items-center gap-2">
        <button
          onClick={() => onEditField(field)}
          className="rounded p-1 hover:bg-gray-100"
          title="編集"
        >
          <LuPencil size={16} className="text-gray-600" />
        </button>
        <button
          onClick={() => onDeleteField(field)}
          className="rounded p-1 hover:bg-gray-100"
          title="削除"
        >
          <LuX size={16} className="text-gray-600" />
        </button>
      </div>
    </div>
  );
}
